import type { ReactNode } from 'react';

import { EmptyState } from '@shared/ui/EmptyState';

import { UploadZone } from '../_shared/components';
import type { useUploadPolicy } from '../_shared/queries';
import type { MediaAsset } from '../_shared/types';
import { AssetRow } from './ContentAssets.AssetRow';

/**
 * A section holding **at most one asset**, its empty state, and its own upload
 * zone. Two surfaces on this page have that shape and no other: the **trailer**,
 * which belongs to a film exactly as much as to a series, and the **film file**,
 * which is the whole of a film's asset list.
 *
 * They were written twice and are drawn once here, because they differ in five
 * strings and one flag and in nothing else. Keeping two copies is how the
 * trailer's empty state and the film's drift apart — and the two are the surface
 * an operator meets when an upload has gone wrong, which is the worst place for
 * the two halves of the page to stop agreeing.
 *
 * **What is NOT parameterised is the free control.** The trailer is free *by
 * kind* and takes a badge with no chip — a chip would claim a control that could
 * never be switched off — while a film's single asset takes neither: an
 * entitlement covers it, and `MAX_PREVIEW_ASSETS` opens the trailer, not the
 * feature. The caller passes the `freeControl` the asset's own kind earns; this
 * component chooses nothing.
 *
 * The season shape does **not** come through here. A season is a partition over
 * an ordered list, carries reorder handles and a season-wide chip, and shares no
 * markup with a single row — `ContentAssets.SeriesPane.tsx` owns it.
 */
export function SingleAssetPane({
  sectionId,
  heading,
  badge,
  lede,
  asset,
  emptyMessage,
  freeControl,
  uploadPolicy,
  busyDocument,
  accept,
  dropLabel,
  dropHint,
  uploadProgress,
  uploadError,
  onFiles,
  onEdit,
  onRemove,
  onAddDocument,
  onRemoveDocument,
}: {
  /** Also the `aria-labelledby` target, so the heading names its own section. */
  sectionId: string;
  heading: string;
  /** The trailer's «free by kind» state badge. Absent on the film. */
  badge?: ReactNode;
  lede: string;
  asset: MediaAsset | null;
  emptyMessage: string;
  freeControl: 'always-free' | 'none';
  uploadPolicy: ReturnType<typeof useUploadPolicy>['data'];
  busyDocument: boolean;
  accept: string;
  dropLabel: string;
  dropHint: string;
  /** This zone's own upload, `0..1`, or `null` when nothing is in flight. */
  uploadProgress: number | null;
  /** This zone's own refusal, drawn under it rather than page-wide. */
  uploadError: string | null;
  onFiles: (files: FileList) => void;
  onEdit: (asset: MediaAsset) => void;
  onRemove: (asset: MediaAsset) => void;
  onAddDocument: (asset: MediaAsset, file: File) => void;
  onRemoveDocument: (asset: MediaAsset, documentId: string, label: string) => void;
}) {
  return (
    <section aria-labelledby={sectionId} className="mb-8 grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id={sectionId} className="font-display text-h2 font-bold">
          {heading}
        </h2>
        {badge}
      </div>

      <p className="mb-0 max-w-measure text-xs text-ink-muted">{lede}</p>

      {asset ? (
        <ul className="grid list-none gap-3 p-0">
          <AssetRow
            asset={asset}
            uploadPolicy={uploadPolicy}
            ordinal={null}
            canReorder={false}
            freeControl={freeControl}
            busyDocument={busyDocument}
            onEdit={() => onEdit(asset)}
            onRemove={() => onRemove(asset)}
            onMove={() => undefined}
            onToggleFree={() => undefined}
            onAddDocument={(file) => onAddDocument(asset, file)}
            onRemoveDocument={(documentId, label) => onRemoveDocument(asset, documentId, label)}
          />
        </ul>
      ) : (
        <EmptyState message={emptyMessage} />
      )}

      {/* One zone per destination, so the drop *is* the assignment — and the
          progress and the refusal belong to that same zone for the same reason. */}
      <UploadZone
        compact
        label={dropLabel}
        accept={accept}
        hint={dropHint}
        progress={uploadProgress}
        error={uploadError}
        onFiles={onFiles}
      />
    </section>
  );
}
